import type { ForkTemplateSchema } from "@gearbox-protocol/deploy-tools-shared";
import type { ButtonBarItem } from "@gearbox-protocol/deploy-tools-ui";
import {
  Button,
  ButtonBar,
  ErrorBox,
  Form,
} from "@gearbox-protocol/deploy-tools-ui";
import { FileUpload } from "components/file-upload";
import type { FC } from "react";
import type { FileWithPath } from "react-dropzone";
import { useNavigate } from "react-router-dom";
import { preventSubmitOnEnter } from "utils";

import { ForkConstructor } from "./ForkConstructor";
import ForkYaml from "./ForkYaml";
import type { EditMode } from "./useForkForm";
import { useForkForm } from "./useForkForm";

const newForkNav: ButtonBarItem<EditMode>[] = [
  {
    id: "form",
    title: "Constructor",
  },
  {
    id: "raw",
    title: "Raw YML",
  },
];

export interface ForkFormProps {
  hideAlias?: boolean;
  title: string;
  submitButtonText: string;
  defaultValues: ForkTemplateSchema;
  defaultFiles?: FileWithPath[];
  onSubmit: (data: ForkTemplateSchema, files: FileWithPath[]) => Promise<void>;
}

export const ForkForm: FC<ForkFormProps> = props => {
  const {
    hideAlias,
    title,
    submitButtonText,
    defaultValues,
    defaultFiles,
    onSubmit,
  } = props;

  const { form, mode, setMode, submit, code, setCode, error, files, setFiles } =
    useForkForm({
      defaultValues,
      defaultFiles,
      onSubmit,
    });

  const navigate = useNavigate();

  return (
    <div className="flex flex-col flex-1 rounded-xl bg-background p-4 gap-y-2">
      <h2>{title}</h2>

      <ButtonBar
        items={newForkNav}
        activeId={mode}
        onChange={setMode}
        buttonClassName="py-1! bg-white/10 hover:bg-black/10 has-checked:bg-black/20"
      />

      <Form {...form}>
        <form
          onSubmit={submit}
          onKeyDown={preventSubmitOnEnter}
          className="space-y-2"
        >
          {mode === "raw" ? (
            <ForkYaml code={code} setCode={setCode} />
          ) : (
            <ForkConstructor form={form} hideAlias={hideAlias} />
          )}

          <FileUpload
            files={files}
            onChange={setFiles}
            accept={{ "application/json": [".json"] }}
          />

          {!!error && <ErrorBox>{error}</ErrorBox>}

          <Button
            type="submit"
            disabled={form.formState.isSubmitting}
            className="self-start w-64"
            loading={form.formState.isSubmitting}
          >
            {submitButtonText}
          </Button>

          <Button
            type="button"
            variant="secondary"
            className="w-64 ml-2"
            onClick={() => navigate(-1)}
          >
            Cancel
          </Button>
        </form>
      </Form>
    </div>
  );
};
